import React, { useEffect, useRef, useState } from "react";

import { IonItem, IonLabel, IonInput, IonList, IonSpinner, useIonToast } from "@ionic/react";

import classNames from "classnames/bind";
import styles from "./SearchAddressInput.module.scss";

import { ParhatoAddressObject } from "@core/types";
import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import commonFormStyles from "../UI/CommonFormElements/CommonFormElements.module.scss";
import { getFullAddress } from "../../utils/utils";
import { useAddressSuggestionMutation } from "../../providers/address";
import { AddressSuggestion } from "@core/types";
import { useTranslation } from "react-i18next";

export namespace SearchAddressInputTypes {
  export type Props = {
    setUserDraggingMap?: (bool: boolean) => void;
    onOptionSelect: (geoObj: SelectedOption) => void;
    searchPrefix?: string;
    disabled?: boolean;
    invalid?: boolean;
    address: string;
    errorMessage?: string;
    handleChangeValue?: (inputAddress: string) => void;
    label?: string;
    isRequired?: boolean;
    wrapperItemStyles?: string;
  };

  export type SelectedOption = {
    address?: ParhatoAddressObject;
  };
}

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

const SearchAddressInput: React.FC<SearchAddressInputTypes.Props> = ({
  setUserDraggingMap,
  onOptionSelect,
  address,
  label,
  isRequired,
  wrapperItemStyles,
  handleChangeValue,
  errorMessage,
  searchPrefix = "",
  disabled = false,
  invalid = false,
}) => {
  const { t } = useTranslation();
  const [presentToast] = useIonToast();

  const [addresses, setAddresses] = useState<AddressSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [isRequestFinished, setIsRequestFinished] = useState(false);
  const [isInputInFocus, setIsInputInFocus] = useState(false);

  const [inputAddress, setInputAddress] = useState("");

  const wrapperRef = useRef<HTMLDivElement>(null);

  const lastReqAbortController = useRef<AbortController | null>(null);
  const debounceTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const { mutateAsync: asyncAddressSuggestionMutation } = useAddressSuggestionMutation();

  useEffect(() => {
    if (!inputAddress || address !== inputAddress) {
      handleAddressInput(inputAddress);
    }
  }, [inputAddress]);

  useEffect(() => {
    setInputAddress(address);
  }, [address]);

  useEffect(() => {
    setAddresses([]);
  }, [searchPrefix]);

  function getAddresses(s: string) {
    setIsRequestFinished(false);

    const abortController = new AbortController();
    lastReqAbortController.current = abortController;

    const addressSuggestionVars = { address: s, searchPrefix, abortSignal: abortController.signal };

    asyncAddressSuggestionMutation(addressSuggestionVars)
      .then((data) => {
        if (data) {
          setAddresses(data);
          setIsRequestFinished(true);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setIsRequestFinished(true);
          setShowDropdown(false);
          presentToast({
            message: t("Address search error, please select a location on the map"),
            duration: 2000,
            color: "danger",
          });
        }
      });
  }

  function handleAddressInput(value: string) {
    lastReqAbortController.current?.abort();
    if (debounceTimeoutId.current) {
      clearTimeout(debounceTimeoutId.current);
    }

    debounceTimeoutId.current = setTimeout(() => {
      if (value && !isOptionSelected) {
        setAddresses([]);
        getAddresses(value);
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
      setIsOptionSelected(false);
      debounceTimeoutId.current = null;
    }, 1000);
  }

  function handleDropdownItemClick(selectedAddress: ParhatoAddressObject) {
    setShowDropdown(false);
    setIsOptionSelected(true);
    onOptionSelect({ address: selectedAddress });
    setUserDraggingMap && setUserDraggingMap(false);
    setInputAddress(getFullAddress(selectedAddress));
  }

  const handleInputAddressChange = (value: string) => {
    setInputAddress(value);
    handleChangeValue?.(value);
  };

  return (
    <div className={cx("dropdown_wrapper")} ref={wrapperRef}>
      <CommonFormElement
        errorMessage={errorMessage}
        isRequired={isRequired}
        classListItem={wrapperItemStyles}
        label={label}
        isValid={!invalid || (isInputInFocus && showDropdown)}
      >
        <IonInput
          className={commonForm(
            "ion-no-padding",
            "commonElement",
            { commonInvalid: invalid },
            "commonElementMaskedInput",
          )}
          value={inputAddress}
          type="text"
          name="search-address"
          required={!disabled}
          onIonChange={(e) => handleInputAddressChange(e.detail.value!)}
          disabled={disabled}
          placeholder={"Адрес"}
          onIonFocus={() => {
            setShowDropdown(addresses.length > 0);
            setIsInputInFocus(true);
          }}
          onIonBlur={(e) => {
            if (e.detail.relatedTarget instanceof Element && !wrapperRef.current?.contains(e.detail.relatedTarget)) {
              setIsInputInFocus(false);
              setShowDropdown(false);
            }
          }}
        />
      </CommonFormElement>
      {isInputInFocus && showDropdown && (
        <IonList className={cx("dropdown")} tabIndex={0}>
          {addresses.length ? (
            addresses.map((addr) => (
              <IonItem
                key={addr.text}
                className={cx("dropdown_item")}
                type="button"
                detail={false}
                tabIndex={0}
                button
                onClick={() => handleDropdownItemClick(addr)}
              >
                <IonLabel className={cx("dropdown_label")}>
                  {addr.text.replace(`${addr.province}, `, "").replace(`${addr.country}, `, "")}
                </IonLabel>
              </IonItem>
            ))
          ) : isRequestFinished ? (
            <IonItem className={cx("dropdown_item", "ion-text-center")} lines="none">
              <IonLabel>{t("Nothing found")}</IonLabel>
            </IonItem>
          ) : (
            <IonSpinner name="lines" className={cx("dropdown_spinner")} />
          )}
        </IonList>
      )}
    </div>
  );
};

export { SearchAddressInput };
